import { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import type { ConflictItem } from '../../calendar-planning-overlay/model/checkCollisions'
import { ConflictWarning } from '../../calendar-planning-overlay/ui/ConflictWarning'

const POPOVER_WIDTH = 240
const POPOVER_APPROX_HEIGHT = 200
const MOBILE_BREAKPOINT = 540

interface Props {
  anchorRect: DOMRect
  startIso: string
  endIso: string
  conflicts: ConflictItem[]
  isPending: boolean
  /** When set, end = start + fixedDurationMin. The "Bis" field becomes read-only. */
  fixedDurationMin?: number
  onChange: (startIso: string, endIso: string) => void
  onSave: () => void
  onCancel: () => void
}

function toTimeValue(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function applyTime(baseIso: string, timeValue: string): string {
  const [h, m] = timeValue.split(':').map(Number)
  const d = new Date(baseIso)
  d.setHours(h, m, 0, 0)
  return d.toISOString()
}

interface Position {
  top: number
  left: number
  width: number
}

function computePosition(anchor: DOMRect): Position {
  const vw = window.innerWidth
  const vh = window.innerHeight

  // Mobile: center horizontally, pin below (or above) the tapped block
  if (vw < MOBILE_BREAKPOINT) {
    const margin = 12
    const width = Math.min(POPOVER_WIDTH, vw - margin * 2)
    const left = Math.round((vw - width) / 2)

    // Prefer below the block; flip above if not enough space
    let top = anchor.bottom + 8
    if (top + POPOVER_APPROX_HEIGHT > vh - margin) {
      top = anchor.top - POPOVER_APPROX_HEIGHT - 8
    }
    top = Math.max(margin, Math.min(top, vh - POPOVER_APPROX_HEIGHT - margin))

    return { top, left, width }
  }

  // Desktop/tablet: right or left of block
  let left = anchor.right + 8
  if (left + POPOVER_WIDTH > vw - 8) {
    left = anchor.left - POPOVER_WIDTH - 8
  }
  left = Math.max(8, Math.min(left, vw - POPOVER_WIDTH - 8))

  let top = anchor.top
  if (top + POPOVER_APPROX_HEIGHT > vh - 8) {
    top = vh - POPOVER_APPROX_HEIGHT - 8
  }
  top = Math.max(8, top)

  return { top, left, width: POPOVER_WIDTH }
}

export function PausePopover({
  anchorRect, startIso, endIso, conflicts, isPending,
  fixedDurationMin, onChange, onSave, onCancel,
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const { top, left, width } = computePosition(anchorRect)

  // Close on ESC or outside click
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    function handlePointer(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onCancel()
      }
    }
    document.addEventListener('keydown', handleKey)
    document.addEventListener('pointerdown', handlePointer, true)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('pointerdown', handlePointer, true)
    }
  }, [onCancel])

  const startValue = toTimeValue(startIso)
  const endValue = toTimeValue(endIso)
  const canSave = conflicts.length === 0 && !isPending

  const content = (
    <div
      ref={ref}
      className="fixed bg-white rounded-xl shadow-xl border border-gray-200 z-[9999] overflow-hidden"
      style={{ top, left, width }}
    >
      <div className="px-4 pt-3 pb-2">
        <p className="text-sm font-semibold text-gray-800 mb-3">Pause verschieben</p>

        <div className="space-y-2 mb-3">
          <label className="flex flex-col gap-0.5">
            <span className="text-xs text-gray-500">Von</span>
            <input
              type="time"
              step="900"
              value={startValue}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              onChange={(e) => {
                if (!e.target.value) return
                const newStart = applyTime(startIso, e.target.value)
                if (fixedDurationMin !== undefined) {
                  // End is always start + fixed duration
                  const newEnd = new Date(new Date(newStart).getTime() + fixedDurationMin * 60_000).toISOString()
                  onChange(newStart, newEnd)
                } else {
                  onChange(newStart, endIso)
                }
              }}
            />
          </label>

          <label className="flex flex-col gap-0.5">
            <span className="text-xs text-gray-500">Bis</span>
            <input
              type="time"
              step="900"
              value={endValue}
              readOnly={fixedDurationMin !== undefined}
              className={`w-full text-sm border rounded-lg px-2 py-1.5 focus:outline-none ${
                fixedDurationMin !== undefined
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-default'
                  : 'border-gray-300 focus:ring-2 focus:ring-blue-400'
              }`}
              onChange={
                fixedDurationMin !== undefined
                  ? undefined
                  : (e) => {
                      if (!e.target.value) return
                      const newEnd = applyTime(endIso, e.target.value)
                      onChange(startIso, newEnd)
                    }
              }
            />
            {fixedDurationMin !== undefined && (
              <span className="text-[10px] text-gray-400">Pause ist immer {fixedDurationMin} Min.</span>
            )}
          </label>
        </div>

        {conflicts.length > 0 && (
          <div className="mb-2">
            <ConflictWarning conflicts={conflicts} />
          </div>
        )}

        <div className="flex gap-2">
          <button
            className="flex-1 text-xs py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            onClick={onCancel}
          >
            Abbrechen
          </button>
          <button
            className={`flex-1 text-xs py-1.5 rounded-lg font-semibold transition-colors ${
              canSave
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            disabled={!canSave}
            onClick={onSave}
          >
            {isPending ? '…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(content, document.body)
}
